import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { InputField } from 'src/components/input-field';
import { pincodeApi } from 'src/api/pincode';
import { emailRegex, phoneRegExp, pincodeRegex } from 'src/components/common/common-regex';
import { Technician, TechnicianBasicProfile } from 'src/interfaces/new-technician/technician';
import { technicianApi } from 'src/api/newTechnician/technician';

interface EditBasicInfoProps {
  open: boolean;
  onClose: () => void;
  technicianDetails: Technician;
  updateBasicDetails: (data: TechnicianBasicProfile) => Promise<boolean>;
}

export const EditBasicInfo: FC<EditBasicInfoProps> = (props) => {
  const { open, onClose, technicianDetails, updateBasicDetails } = props;
  const [checkCAPincode, setCheckCAPincode] = useState<boolean>(false);
  const [checkPAPincode, setCheckPAPincode] = useState<boolean>(false);
  const [isEmailExists, setIsEmailExist] = useState<boolean>(false);
  const [mobileDuplicate, setMobileDuplicate] = useState<boolean>(false);
  const [addressDisable, setAddressDisable] = useState<boolean>(false);
  const [cAAreas, setCAAreas] = useState([]);
  const [pAAreas, setPAAreas] = useState([]);
  const stringRegExp = /^[A-Za-z ]+$/;

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: technicianDetails?.firstName,
      lastName: technicianDetails?.lastName,
      email: technicianDetails?.email,
      mobile: technicianDetails?.mobile,
      currentAddress: {
        addressLine1: technicianDetails?.currentAddress?.addressLine1,
        addressLine2: technicianDetails?.currentAddress?.addressLine2,
        landmark: technicianDetails?.currentAddress?.landmark,
        pincode: technicianDetails?.currentAddress?.pincode,
        city: technicianDetails?.currentAddress?.city,
        state: technicianDetails?.currentAddress?.state,
        area: technicianDetails?.currentAddress?.area,
      },
      permanentAddress: {
        addressLine1: technicianDetails?.permanentAddress?.addressLine1,
        addressLine2: technicianDetails?.permanentAddress?.addressLine2,
        landmark: technicianDetails?.permanentAddress?.landmark,
        pincode: technicianDetails?.permanentAddress?.pincode,
        city: technicianDetails?.permanentAddress?.city,
        state: technicianDetails?.permanentAddress?.state,
        area: technicianDetails?.permanentAddress?.area,
      },
      submit: null
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().max(255).required('First Name is required').matches(new RegExp(stringRegExp),
        'Only alphabets and spaces allowed'),
      lastName: Yup.string().max(255).required('First Name is required').matches(new RegExp(stringRegExp),
        'Only alphabets and spaces allowed'),
      email: Yup.string().email('Invalid email').max(255).optional(),
      mobile: Yup.string().matches(phoneRegExp, 'Phone Number is not valid').required('Mobile Number is required'),
      currentAddress: Yup.object().shape({
        addressLine1: Yup.string().required('Address Line 1 is required'),
        addressLine2: Yup.string().required('Address Line 2 is required'),
        landmark: Yup.string().optional(),
        area: Yup.string().required('Area is required'),
        pincode: Yup.number().moreThan(0).min(100000, 'Not a valid Pincode')
          .max(999999, 'Not a valid Pincode')
          .required('Valid Pincode is required')
          .typeError('Valid Pincode is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required')
      }),
      permanentAddress: Yup.object().shape({
        addressLine1: Yup.string().required('Address Line 1 is required'),
        addressLine2: Yup.string().required('Address Line 2 is required'),
        landmark: Yup.string().optional(),
        area: Yup.string().required('Area is required'),
        pincode: Yup.number().moreThan(0).min(100000, 'Not a valid Pincode')
          .max(999999, 'Not a valid Pincode')
          .required('Valid Pincode is required')
          .typeError('Valid Pincode is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required')
      }),
    }),

    onSubmit: async (values, helpers) => {
      const editTechnician: Technician = {
        channelPartner: technicianDetails?.channelPartner,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile,
        currentAddress: {
          city: values.currentAddress.city,
          addressLine1: values.currentAddress.addressLine1,
          addressLine2: values.currentAddress.addressLine2,
          landmark: values.currentAddress.landmark,
          pincode: values.currentAddress.pincode,
          area: values.currentAddress.area,
          state: values.currentAddress.state
        },
        permanentAddress: {
          city: values.permanentAddress.city,
          addressLine1: values.permanentAddress.addressLine1,
          addressLine2: values.permanentAddress.addressLine2,
          landmark: values.permanentAddress.landmark,
          pincode: values.permanentAddress.pincode,
          area: values.permanentAddress.area,
          state: values.permanentAddress.state
        }
      };
      helpers.resetForm();
      const resultStatus = await updateBasicDetails(editTechnician);
      if (resultStatus) {
        closeDialog('', '');
      }
    }
  });

  const handleEmailExist = async (event) => {
    const enteredEmail = event.target.value?.trim();
    formik.setFieldValue('email', event.target.value);
    if (enteredEmail !== '' && emailRegex.test(enteredEmail) && !formik.errors.email) {
      await formik.setFieldTouched('email', true);
      const responseOfEmail = await technicianApi
        .getTechnicianByEmail(enteredEmail);
      if (responseOfEmail?.responseData?.length
        && responseOfEmail?.responseData?.id !== technicianDetails?.id
        && !responseOfEmail?.responseData?.message) {
        setIsEmailExist(true);
        await formik.setFieldError('email', 'This email is already in use');
      } else {
        await formik.setFieldError('email', undefined);
        setIsEmailExist(false);
      }
    } else {
      await formik.setFieldTouched('email', true);
    }
  };

  const setPincodeDetails = (pincodes, address) => {
    if (address === 'currentAddress') {
      // Set city
      formik.setFieldValue('currentAddress.city', pincodes[0].city);
      // set areas for dropdown
      const areaList = [];
      // get areas from each pincode onject
      pincodes.forEach((pincode) => areaList.push(pincode.area));
      // set areas using distinct values from areaList
      setCAAreas([...new Set(areaList)]);
      formik.setFieldValue('currentAddress.state', pincodes[0]?.state);
    } else {
      formik.setFieldValue('permanentAddress.city', pincodes[0].city);
      const areaList = [];
      pincodes.forEach((pincode) => areaList.push(pincode.area));
      setPAAreas([...new Set(areaList)]);
      formik.setFieldValue('permanentAddress.state', pincodes[0]?.state);
    }
  };

  const getPincodeData = async (pincode: string | number, address: string) => {
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'code',
          operator: 'equal',
          value: [
            pincode,
          ],
        },
      ],
      pageSize: 9999,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };
    const result = await pincodeApi.getPincodesByPaginationAndFilters(paginationObject);
    if (result.responseData.totalRecords) {
      setPincodeDetails(result.responseData.records, address);
    }
    return result.responseData.records;
  };

  const handlePincodeChange = async (event, address) => {
    if (address === 'currentAddress') {
      formik.setFieldValue('currentAddress.pincode', event.target.value);
    } else {
      formik.setFieldValue('permanentAddress.pincode', event.target.value);
    }
    if (pincodeRegex.test(event.target.value?.toString())) {
      if (address === 'currentAddress') {
        await formik.setFieldTouched('currentAddress.pincode', true);
      } else {
        await formik.setFieldTouched('permanentAddress.pincode', true);
      }
      const result = await getPincodeData(event.target.value, address);
      if (result.length) {
        formik.handleChange(event);
        if (address === 'currentAddress') {
          setCheckCAPincode(false);
          await formik.setFieldError('currentAddress.pincode', undefined);
        } else {
          setCheckPAPincode(false);
          await formik.setFieldError('currentAddress.pincode', undefined);
        }
      } else if (address === 'currentAddress') {
        setCheckCAPincode(true);
        await formik.setFieldError('currentAddress.pincode', 'Unavailable Pincode');
        setCAAreas([]);
        formik.setFieldValue('currentAddress.city', '');
        formik.setFieldValue('currentAddress.area', '');
        formik.setFieldValue('currentAddress.state', '');
        formik.setFieldTouched('currentAddress.area', false);
      } else {
        setCheckPAPincode(true);
        await formik.setFieldError('permanentAddress.pincode', 'Unavailable Pincode');
        setPAAreas([]);
        formik.setFieldValue('permanentAddress.city', '');
        formik.setFieldValue('permanentAddress.area', '');
        formik.setFieldValue('permanentAddress.state', '');
        formik.setFieldTouched('permanentAddress.area', false);
      }
    } else if (address === 'currentAddress') {
      await formik.setFieldTouched('currentAddress.pincode', false);
      setCAAreas([]);
      formik.setFieldValue('currentAddress.city', '');
      formik.setFieldValue('currentAddress.area', '');
      formik.setFieldValue('currentAddress.state', '');
      formik.setFieldTouched('currentAddress.area', false);
    } else {
      await formik.setFieldTouched('permanentAddress.pincode', false);
      setPAAreas([]);
      formik.setFieldValue('permanentAddress.city', '');
      formik.setFieldValue('permanentAddress.area', '');
      formik.setFieldValue('permanentAddress.state', '');
      formik.setFieldTouched('permanentAddress.area', false);
    }
  };

  const handleMobileExist = async (event) => {
    const enteredMobile = event.target.value?.trim();
    formik.setFieldValue('mobile', event.target.value);
    if (phoneRegExp.test(enteredMobile)) {
      await formik.setFieldTouched('mobile', true);
      const responseOfMobile = await technicianApi
        .getTechnicianByMobile(enteredMobile);
      if (responseOfMobile?.responseData?.length
        && responseOfMobile?.responseData?.id !== technicianDetails?.id
        && !responseOfMobile?.responseData?.message) {
        setMobileDuplicate(true);
        await formik.setFieldError('mobile', 'This Mobile Number is already in use');
      } else {
        await formik.setFieldError('mobile', undefined);
        setMobileDuplicate(false);
      }
    } else {
      await formik.setFieldTouched('mobile', true);
      formik.handleChange(event);
    }
  };

  const copyAddress = (e) => {
    const { checked } = e.target;
    if (checked) {
      setCheckPAPincode(false);
      setPAAreas(cAAreas);
      formik.values.permanentAddress = { ...formik.values.currentAddress };
      setAddressDisable(true);
      formik.setErrors({ permanentAddress: undefined });
      formik.setTouched({ permanentAddress: undefined });
    } else {
      setPAAreas([]);
      formik.setFieldValue('permanentAddress.addressLine1', '');
      formik.setFieldValue('permanentAddress.addressLine2', '');
      formik.setFieldValue('permanentAddress.landmark', '');
      formik.setFieldValue('permanentAddress.pincode', '');
      formik.setFieldValue('permanentAddress.area', '');
      formik.setFieldValue('permanentAddress.city', '');
      formik.setFieldValue('permanentAddress.state', '');
      setAddressDisable(false);
    }
  };

  useEffect(() => {
    getPincodeData(technicianDetails?.currentAddress?.pincode, 'currentAddress');
    getPincodeData(technicianDetails?.permanentAddress?.pincode, 'permanentAddress');
  }, [technicianDetails?.currentAddress?.pincode, technicianDetails?.permanentAddress?.pincode]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{
          justifyContent: 'flex-end',
          mb: -3,
          mt: -2
        }}
      >
        Edit Basic Profile Details
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ justifyItems: 'center' }}
      >
        <Grid
          container
          sx={{
            mt: 1,
          }}
          spacing={1}
        >
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                fullWidth
                label="Channel Partner Name"
                disabled
                value={technicianDetails?.channelPartner?.name}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                fullWidth
                label="Channel Partner Id"
                disabled
                value={technicianDetails?.channelPartner?.id}
                sx={{ mb: 1 }}
              />
            </Grid>

          </Grid>
          <Grid
            container
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.firstName
                        && formik.errors.firstName)}
                fullWidth
                helperText={formik.touched.firstName
                        && formik.errors.firstName}
                placeholder="Enter First Name"
                label="First Name"
                name="firstName"
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.firstName}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.lastName
                        && formik.errors.lastName)}
                fullWidth
                helperText={formik.touched.lastName
                        && formik.errors.lastName}
                placeholder="Enter Last Name"
                label="Last Name"
                name="lastName"
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.lastName}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.email
                        && formik.errors.email)}
                fullWidth
                helperText={formik.touched.email
                        && formik.errors.email}
                placeholder="Enter Email"
                name="email"
                label="Email Id"
                onChange={formik.handleChange}
                onBlur={handleEmailExist}
                sx={{ mb: 1 }}
                value={formik.values.email}
              />
              { isEmailExists && !formik.errors.email && (
              <Grid
                sx={{
                  ml: '13px',
                  mt: '-9px'
                }}
              >
                <Typography
                  lineHeight={0.5}
                  sx={{
                    fontWeight: '1'
                  }}
                  variant="caption"
                  color="red"
                >
                  This email is already in use
                </Typography>
              </Grid>
              )}
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <>
                <InputField
                  error={Boolean(formik.touched.mobile
                        && formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                  fullWidth
                  required
                  label="Mobile Number"
                  name="mobile"
                  onChange={formik.handleBlur}
                  onBlur={handleMobileExist}
                  placeholder="Enter Mobile Number"
                  value={formik.values.mobile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                      >
                        +91
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                { mobileDuplicate && !formik.errors.mobile && (
                <Grid
                  sx={{
                    ml: '13px',
                    mt: '-9px'
                  }}
                >
                  <Typography
                    lineHeight={0.5}
                    sx={{
                      fontWeight: '1'
                    }}
                    variant="caption"
                    color="red"
                  >
                    This Mobile Number is already in use
                  </Typography>
                </Grid>
                )}
              </>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Grid
              item
              md={12}
              sm={12}
              xs={12}
            >
              <Typography
                color="colorTextPrimary"
                variant="h6"
              >
                Current Address
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.addressLine1
                        && formik.errors.currentAddress?.addressLine1)}
                fullWidth
                helperText={formik.touched.currentAddress?.addressLine1
                        && formik.errors.currentAddress?.addressLine1}
                label="Address Line 1"
                placeholder="Enter Address Line 1"
                name="currentAddress.addressLine1"
                required
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.currentAddress.addressLine1}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.addressLine2
                        && formik.errors.currentAddress?.addressLine2)}
                fullWidth
                helperText={formik.touched.currentAddress?.addressLine2
                        && formik.errors.currentAddress?.addressLine2}
                label="Address Line 2"
                placeholder="Enter Address Line 2"
                name="currentAddress.addressLine2"
                required
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.currentAddress.addressLine2}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.landmark
                        && formik.errors.currentAddress?.landmark)}
                fullWidth
                helperText={formik.touched.currentAddress?.landmark
                        && formik.errors.currentAddress?.landmark}
                label="Landmark"
                placeholder="Enter landmark "
                name="currentAddress.landmark"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.currentAddress.landmark}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            />
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.pincode
                        && formik.errors.currentAddress?.pincode)}
                fullWidth
                helperText={formik.touched.currentAddress?.pincode
                        && formik.errors.currentAddress?.pincode}
                placeholder="Type and Press Enter"
                name="currentAddress.pincode"
                label="Pincode"
                required
                onChange={(e) => { handlePincodeChange(e, 'currentAddress'); }}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.currentAddress.pincode}
              />
              { checkCAPincode && !formik.errors.currentAddress?.pincode && (
                <Typography
                  sx={{
                    ml: 2
                  }}
                  variant="caption"
                  color="red"
                >
                  Unavailable Pincode
                </Typography>
              )}
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.area
                        && formik.errors.currentAddress?.area)}
                fullWidth
                helperText={formik.touched.currentAddress?.area
                        && formik.errors.currentAddress?.area}
                placeholder="Enter Area"
                label="Area"
                name="currentAddress.area"
                disabled={!cAAreas.length}
                required
                select
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.currentAddress.area}
              >
                {cAAreas?.map((area) => (
                  <MenuItem
                    key={area}
                    value={area}
                  >
                    {area}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.city
                        && formik.errors.currentAddress?.city)}
                helperText={formik.touched.currentAddress?.city
                        && formik.errors.currentAddress?.city}
                fullWidth
                required
                disabled
                label="City"
                name="currentAddress.city"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter city"
                value={formik.values.currentAddress.city}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.currentAddress?.state
                        && formik.errors.currentAddress?.state)}
                helperText={formik.touched.currentAddress?.state
                        && formik.errors.currentAddress?.state}
                fullWidth
                disabled
                required
                name="currentAddress.state"
                label="State"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter state"
                value={formik.values.currentAddress.state}
                sx={{ mb: 1 }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              md={2}
              sm={12}
              xs={12}
            >
              <Typography
                color="colorTextPrimary"
                variant="h6"
              >
                Permanent Address
              </Typography>
            </Grid>
            <Grid
              item
              md={10}
              sm={12}
              xs={12}
            >
              <Checkbox
                sx={{
                  mt: -1
                }}
                disabled={(formik.values.currentAddress.addressLine1 === ''
                      || formik.values.currentAddress.addressLine2 === ''
                      || formik.values.currentAddress.pincode === null
                      || formik.values.currentAddress.area === '')}
                onClick={(e) => { copyAddress(e); }}
              />
              <Typography
                sx={{
                  mt: -1,
                }}
                display="inline-block"
                variant="body2"
              >
                Same as Current Address
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.addressLine1
                        && formik.errors.permanentAddress?.addressLine1)}
                fullWidth
                helperText={formik.touched.permanentAddress?.addressLine1
                        && formik.errors.permanentAddress?.addressLine1}
                placeholder="Enter Address Line 1"
                label="Address Line 1"
                name="permanentAddress.addressLine1"
                required
                disabled={addressDisable}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.permanentAddress.addressLine1}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.addressLine2
                        && formik.errors.permanentAddress?.addressLine2)}
                fullWidth
                helperText={formik.touched.permanentAddress?.addressLine2
                        && formik.errors.permanentAddress?.addressLine2}
                placeholder="Enter Address Line 2"
                label="Address Line 2"
                name="permanentAddress.addressLine2"
                required
                disabled={addressDisable}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.permanentAddress.addressLine2}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.landmark
                        && formik.errors.permanentAddress?.landmark)}
                fullWidth
                helperText={formik.touched.permanentAddress?.landmark
                        && formik.errors.permanentAddress?.landmark}
                label="Landmark"
                placeholder="Enter landmark "
                name="permanentAddress.landmark"
                onChange={formik.handleChange}
                disabled={addressDisable}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.permanentAddress.landmark}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            />
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.pincode
                        && formik.errors.permanentAddress?.pincode)}
                fullWidth
                helperText={formik.touched.permanentAddress?.pincode
                        && formik.errors.permanentAddress?.pincode}
                placeholder="Type and Press Enter"
                label="Pincode"
                name="permanentAddress.pincode"
                required
                disabled={addressDisable}
                onChange={(e) => { handlePincodeChange(e, 'permanentAddress'); }}
                onBlur={formik.handleBlur}
                sx={{ mb: 1 }}
                value={formik.values.permanentAddress.pincode}
              />
              { checkPAPincode && !formik.errors.permanentAddress?.pincode && (
                <Typography
                  sx={{
                    ml: 2
                  }}
                  variant="caption"
                  color="red"
                >
                  Unavailable Pincode
                </Typography>
              )}
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.area
                        && formik.errors.permanentAddress?.area)}
                fullWidth
                helperText={formik.touched.permanentAddress?.area
                        && formik.errors.permanentAddress?.area}
                label="Area"
                placeholder="Enter Area"
                name="permanentAddress.area"
                required
                disabled={addressDisable || !pAAreas.length}
                select
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values?.permanentAddress.area}
              >
                {pAAreas?.map((area) => (
                  <MenuItem
                    key={area}
                    value={area}
                  >
                    {area}
                  </MenuItem>
                ))}
              </InputField>
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.city
                        && formik.errors.permanentAddress?.city)}
                helperText={formik.touched.permanentAddress?.city
                        && formik.errors.permanentAddress?.city}
                fullWidth
                required
                disabled
                label="City"
                name="permanentAddress.city"
                onChange={(event) => {
                  if (event.target.value !== ' ') {
                    formik.setFieldValue('permanentAddress.city', event.target.value);
                  }
                }}
                onBlur={formik.handleBlur}
                placeholder="Enter city"
                value={formik.values.permanentAddress.city}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.permanentAddress?.state
                        && formik.errors.permanentAddress?.state)}
                helperText={formik.touched.permanentAddress?.state
                        && formik.errors.permanentAddress?.state}
                fullWidth
                required
                label="State"
                disabled
                name="permanentAddress.state"
                onChange={(event) => {
                  if (event.target.value !== ' ') {
                    formik.setFieldValue('permanentAddress.state', event.target.value);
                  }
                }}
                onBlur={formik.handleBlur}
                placeholder="Enter state"
                value={formik.values.permanentAddress.state}
                sx={{ mb: 1 }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={(!(formik.isValid && formik.dirty) || formik.isSubmitting)
            || isEmailExists || checkCAPincode || checkPAPincode}
          color="primary"
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditBasicInfo.defaultProps = {
  open: false,
};

EditBasicInfo.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // @ts-ignore
  technicianDetails: PropTypes.object.isRequired,
  updateBasicDetails: PropTypes.func
};
